import { Button, cn } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { AiChatSessionSummary, AiGenerationAssetSummary } from '../types';
import { groupByDate } from '../utils/group-by-date';

export type AiChatHistorySheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessions: AiChatSessionSummary[];
  generationAssets: AiGenerationAssetSummary[];
  activeSessionId?: string | null;
  onSelectSession: (sessionId: string) => void;
  onSelectGeneration?: (assetId: string) => void;
  isLoadingSessions?: boolean;
  isLoadingGenerations?: boolean;
};

function truncatePrompt(text: string, max = 24): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

export function AiChatHistorySheet({
  open,
  onOpenChange,
  sessions,
  generationAssets,
  activeSessionId,
  onSelectSession,
  onSelectGeneration,
  isLoadingSessions,
  isLoadingGenerations,
}: AiChatHistorySheetProps) {
  const generationGroups = groupByDate(
    generationAssets.filter((item) => item.prompt?.trim())
  );

  return (
    <Sheet isOpen={open} placement="right" onOpenChange={onOpenChange}>
      <Sheet.Backdrop>
        <Sheet.Content className="w-[min(720px,92vw)]">
          <Sheet.Dialog className="flex h-[100dvh] flex-col">
            <Sheet.Header className="shrink-0">
              <Sheet.Heading>历史记录</Sheet.Heading>
            </Sheet.Header>
            <Sheet.Body className="min-h-0 flex-1 overflow-hidden p-0">
              <div className="grid h-full min-h-0 grid-cols-2">
                <section className="border-border/60 flex min-h-0 flex-col border-r">
                  <div className="border-border/60 text-foreground border-b px-4 py-3 text-sm font-medium">
                    会话记录
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                    {isLoadingSessions ? (
                      <p className="text-muted px-2 py-3 text-sm">加载中…</p>
                    ) : sessions.length === 0 ? (
                      <p className="text-muted px-2 py-3 text-sm">暂无会话</p>
                    ) : (
                      sessions.map((session) => {
                        const selected = session.id === activeSessionId;
                        return (
                          <Button
                            key={session.id}
                            className={cn(
                              'h-auto w-full justify-start rounded-lg px-3 py-2.5 text-left text-sm font-normal',
                              selected && 'bg-default/80'
                            )}
                            variant="ghost"
                            onPress={() => {
                              onSelectSession(session.id);
                              onOpenChange(false);
                            }}
                          >
                            <span className="line-clamp-2">
                              {session.title?.trim() || '新会话'}
                            </span>
                          </Button>
                        );
                      })
                    )}
                  </div>
                </section>

                <section className="flex min-h-0 flex-col">
                  <div className="border-border/60 text-foreground border-b px-4 py-3 text-sm font-medium">
                    生图记录
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                    {isLoadingGenerations ? (
                      <p className="text-muted px-2 py-3 text-sm">加载中…</p>
                    ) : generationGroups.length === 0 ? (
                      <p className="text-muted px-2 py-3 text-sm">暂无生成记录</p>
                    ) : (
                      generationGroups.map((group) => (
                        <div key={group.label} className="mb-4">
                          <div className="text-foreground px-2 py-1 text-sm font-semibold">
                            {group.label}
                          </div>
                          {group.items.map((item) => (
                            <Button
                              key={item.id}
                              className="h-auto w-full justify-start rounded-lg px-3 py-2 text-left text-sm font-normal"
                              variant="ghost"
                              onPress={() => onSelectGeneration?.(item.id)}
                            >
                              {truncatePrompt(item.prompt ?? '')}
                            </Button>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>
            </Sheet.Body>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
